import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';

import { Meta } from '$partials/global';
import { Header, Footer, StickyCTAButtons } from '$partials/global/index.js';
import { ModuleDispatcher } from '$partials/modules';
import { AjaxListingContext, InfinitePagination } from '$partials/base/ajax-listing.jsx';
import { useAjaxListing } from '$hooks/ajax-listing';
import { ArticleItem } from '$partials/listings/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ArticleListingPage = (props) => {
    const page = props.data.page;

    // references
    const watchedElemRef = useRef();

    // state
    const [ headerHeight, setHeaderHeight ] = useState();

    const listing = useAjaxListing({
        infinite: true,
        items: page?.items?.edges,
        page: page?.items?.pageInfo?.page || 1,
        path: `/api/v1/listings/${encodeURIComponent(props.data.uri)}`,
        totalItems: page?.items?.total,
        totalPages: page?.items?.totalPages,
    });

    return (
        <>
            <Meta
                description={page.searchDescription || page.listingText}
                domain={props.domain}
                image={page.listingImage}
                page={page} />

            <Header
                {...props?.data?.navigation}
                {...props?.data?.ticker}
                headerHeight={(height) => setHeaderHeight(height)}
                pageType={page?.__typename}
                standfirst={page?.standfirst}
                watchedElemRef={watchedElemRef} />

            <StickyCTAButtons
                cta1={page?.stickyCta1?.[0]}
                cta2={page?.stickyCta2?.[0]} />

            <main
                id='main-content'
                style={{marginTop: `${headerHeight}px`}}>
                <section
                    className='layout-container pt-30 sm:pt-20 md:pt-0 mb-60 md:mb-100 title-height'
                    style={{top: `${headerHeight}px`}}>
                    <div className='layout-grid-negative-gutter md:flex'>
                        <div className='md:w-1/12 layout-grid-col-padding' />
                        <div className='sm:w-8/12 md:w-7/12 layout-grid-col-padding'>
                            {page?.title && (
                                <h1 className='type-style-4 pb-30'>{page.title}</h1>
                            )}

                            {page?.heading && (
                                <p className='type-style-1'>{page.heading}</p>
                            )}
                        </div>
                    </div>
                </section>

                <div
                    className='bg-white relative'
                    ref={watchedElemRef}>
                    {page?.modules?.map((module, idx) => {
                        return (
                            <ModuleDispatcher
                                borderTop={true}
                                index={idx}
                                key={idx}
                                {...module} />
                        );
                    })}
                </div>

                <section>
                    <AjaxListingContext.Provider value={listing}>
                        {listing?.items?.length > 0 && listing.items.map(({ node: item }, idx) => {
                            return (
                                <ArticleItem
                                    key={idx}
                                    {...item} />
                            );
                        })}
                        {/* No result message */}
                        {(!listing.items?.length && !listing.loading) && (
                            <section className='layout-container border-t border-t-charcoal py-40'>
                                <div className='layout-grid-negative-gutter md:flex'>
                                    <div className='md:w-1/12 layout-grid-col-padding' />
                                    <div className='md:w-11/12 layout-grid-col-padding'>
                                        <p className='type-style-3'>
                                            {'No results'}
                                        </p>
                                    </div>
                                </div>
                            </section>
                        )}
                        {listing?.page < listing?.totalPages && (
                            <InfinitePagination>
                                <section className='layout-container border-t border-t-charcoal py-40'>
                                    <div className='layout-grid-negative-gutter md:flex'>
                                        <div className='md:w-1/12 layout-grid-col-padding' />
                                        <div className='md:w-11/12 layout-grid-col-padding'>
                                            <p className='type-style-4'>
                                                Loading...
                                            </p>
                                        </div>
                                    </div>
                                </section>
                            </InfinitePagination>
                        )}
                    </AjaxListingContext.Provider>
                </section>

            </main>

            <Footer
                {...props?.data?.footer}
                {...props?.data?.newsletter} />
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.object,
        footer: PropTypes.object,
        navigation: PropTypes.object,
        newsletter: PropTypes.object,
        ticker: PropTypes.object,
        uri: PropTypes.string
    }),
    domain: PropTypes.string,
};

ArticleListingPage.propTypes = propTypes;

export default ArticleListingPage;
